/**
 * Stream B — B10: Audit Log Viewer
 *
 * Filterable view of audit_log table. Real API, no mocks.
 * Filters: date range, action type, actor. Export CSV.
 */
import { useState, useEffect, useCallback } from "react";
import { FileText, Download, Search } from "lucide-react";
import { apiFetch } from "@/lib/api-client";
import { formatDateTime } from "@/lib/i18n";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import {
  CompliancePageHeader,
  ComplianceTenantRequiredState,
  useComplianceTenant,
} from "./shared";

interface AuditEntry {
  id: string;
  timestamp: string;
  actorId: string;
  action: string;
  category: string;
  severity: string;
  correlationId: string | null;
  details: Record<string, unknown>;
}

export default function AuditLogViewerPage() {
  const { tenantId, hasTenant } = useComplianceTenant();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [actorFilter, setActorFilter] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setEntries([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    const params = new URLSearchParams();
    params.set("tenant_id", tenantId);
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo) params.set("to", dateTo);
    if (actionFilter !== "all") params.set("action", actionFilter);
    if (actorFilter) params.set("actor", actorFilter);
    const qs = params.toString();

    apiFetch<{ data: AuditEntry[] }>(`/platform/compliance/audit-log${qs ? `?${qs}` : ""}`)
      .then((res) => setEntries(res.data))
      .catch((err: unknown) => {
        setEntries([]);
        setError(err instanceof Error ? err.message : "Failed to load audit log");
      })
      .finally(() => setIsLoading(false));
  }, [tenantId, dateFrom, dateTo, actionFilter, actorFilter]);

  const handleExportCsv = useCallback(() => {
    const headers = ["Timestamp", "Actor", "Action", "Category", "Correlation ID", "Details"];
    const escape = (v: string) => v.includes(",") || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v;
    const rows = entries.map((e) => [
      e.timestamp,
      e.actorId,
      e.action,
      e.category,
      e.correlationId ?? "",
      JSON.stringify(e.details),
    ].map(escape));
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `audit-log-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [entries]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <CompliancePageHeader
          icon={<FileText className="h-5 w-5 text-[var(--color-primary)]" />}
          title="Audit Log"
          description="Reads tenant-scoped audit entries from the control-plane ledger."
        />
        <button
          onClick={handleExportCsv}
          disabled={entries.length === 0}
          className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)] disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" /> Export CSV
        </button>
      </div>

      {!hasTenant ? <ComplianceTenantRequiredState /> : null}

      {error ? (
        <div className="rounded-lg border border-[var(--color-error)]/30 bg-[var(--color-error)]/5 px-4 py-3 text-sm text-[var(--color-error)]">
          {error}
        </div>
      ) : null}

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]" />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]" />
        <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]">
          <option value="all">All actions</option>
          <option value="create">Create</option>
          <option value="update">Update</option>
          <option value="delete">Delete</option>
          <option value="login">Login</option>
          <option value="export">Export</option>
          <option value="erasure">Erasure</option>
        </select>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-muted-foreground)]" />
          <input value={actorFilter} onChange={(e) => setActorFilter(e.target.value)} placeholder="Filter by actor..." className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] pl-9 pr-3 py-2 text-sm text-[var(--color-foreground)]" />
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-8 w-40" />
          {[1, 2, 3, 4, 5].map((i) => <SkeletonCard key={i} className="h-10" />)}
        </div>
      ) : entries.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-12 text-center">
          <FileText className="h-10 w-10 mx-auto text-[var(--color-muted-foreground)] mb-3" />
          <p className="text-[var(--color-foreground)] font-medium">No audit entries found</p>
          <p className="text-sm text-[var(--color-muted-foreground)] mt-1">Entries appear as users perform actions. Try adjusting your filters.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-[var(--color-border)] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface)] text-left text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
                <th className="px-4 py-3">Time</th>
                <th className="px-4 py-3">Actor</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {entries.map((entry) => (
                <tr key={entry.id} className="stagger-item hover:bg-[var(--color-surface)]/50">
                  <td className="px-4 py-3 text-[var(--color-muted-foreground)] text-xs whitespace-nowrap">{formatDateTime(entry.timestamp)}</td>
                  <td className="px-4 py-3 text-[var(--color-foreground)]">{entry.actorId}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      entry.action.includes("delete") || entry.action.includes("erasure") ? "bg-[var(--color-error)]/10 text-[var(--color-error)]"
                      : entry.action.includes("create") || entry.action.includes("requested") ? "bg-[var(--color-success)]/10 text-[var(--color-success)]"
                      : "bg-[var(--color-surface)] text-[var(--color-muted-foreground)]"
                    }`}>
                      {entry.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[var(--color-muted-foreground)]">{entry.category}</td>
                  <td className="px-4 py-3 text-[var(--color-muted-foreground)] text-xs truncate max-w-[240px]">{JSON.stringify(entry.details)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
